import type { TFunction } from 'i18next';
import type { TimeSlot } from '@minibook/shared';
import { Calendar } from '../../../app/components/Calendar.js';
import { LanguageSwitcher } from '../../../app/components/LanguageSwitcher.js';
import { AppIcon } from '../../ui/Icon';
import { TimeSlotSection } from './TimeSlotSection.js';

type GroupedSlots = { morning: TimeSlot[]; day: TimeSlot[]; evening: TimeSlot[] };

type BookingStep2TimeProps = {
  t: TFunction;
  calendarRevealed: boolean;
  calendarError: string | null;
  calendarRequestId: string | null;
  calendarLoading: boolean;
  availableDates: Set<string> | null;
  occupancyByDate: Record<string, number>;
  selectedDate: string;
  today: string;
  maxDate: string;
  slotsLoading: boolean;
  profileLoading: boolean;
  availableSlots: TimeSlot[];
  groupedSlots: GroupedSlots;
  selectedSlot: TimeSlot | null;
  onBack: () => void;
  onDateSelect: (date: string) => void;
  onSlotSelect: (slot: TimeSlot) => void;
  onMonthChange: (range: { from: string; to: string }) => void;
  formatSlotTime: (isoString: string | undefined | null) => string;
};

export function BookingStep2Time({
  t,
  calendarRevealed,
  calendarError,
  calendarRequestId,
  calendarLoading,
  availableDates,
  occupancyByDate,
  selectedDate,
  today,
  maxDate,
  slotsLoading,
  profileLoading,
  availableSlots,
  groupedSlots,
  selectedSlot,
  onBack,
  onDateSelect,
  onSlotSelect,
  onMonthChange,
  formatSlotTime,
}: BookingStep2TimeProps) {
  return (
    <div className={`booking-step-reveal${calendarRevealed ? ' is-visible' : ''}`}>
      <div
        className="booking-p-md-lg booking-gap-md booking-border-b"
        style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
      >
        <button
          type="button"
          onClick={onBack}
          className="booking-icon-ghost"
          style={{ padding: 'var(--spacing-sm)', color: 'var(--color-text-primary)' }}
        >
          <AppIcon name="chevronLeft" size={24} />
        </button>
        <h1 className="booking-heading" style={{ margin: 0, flex: 1, textAlign: 'left' }}>
          {t('booking.selectDateTime')}
        </h1>
        <LanguageSwitcher />
      </div>

      <div className="booking-mb-xl">
        {calendarError && (
          <div className="booking-calendar-helper booking-calendar-error">
            {calendarError}
            {calendarRequestId && (
              <div className="booking-calendar-request-id">
                {t('booking.requestIdLabel')}: {calendarRequestId}
              </div>
            )}
          </div>
        )}
        {!calendarLoading && !calendarError && availableDates && availableDates.size === 0 && (
          <div className="booking-calendar-helper booking-calendar-helper-empty">
            {t('booking.calendar.noDates')}
          </div>
        )}
        <Calendar
          selectedDate={selectedDate}
          onDateSelect={onDateSelect}
          minDate={today}
          maxDate={maxDate}
          availableDates={availableDates ?? undefined}
          occupancyByDate={occupancyByDate}
          onMonthChange={onMonthChange}
        />
      </div>

      {selectedDate && (
        <div className="booking-time-slots">
          {!profileLoading && !slotsLoading && availableSlots.length === 0 && (
            <div className="booking-empty-state">
              <div className="booking-mb-md booking-color-muted">{t('booking.noSlotsAvailable')}</div>
            </div>
          )}

          {(slotsLoading || availableSlots.length > 0) && (
            <>
              {(groupedSlots.morning.length > 0 || slotsLoading) && (
                <TimeSlotSection
                  title={t('booking.timeOfDay.morning')}
                  slots={groupedSlots.morning}
                  selectedSlot={selectedSlot}
                  onSelect={onSlotSelect}
                  formatTime={formatSlotTime}
                  loading={slotsLoading}
                />
              )}
              {(groupedSlots.day.length > 0 || slotsLoading) && (
                <TimeSlotSection
                  title={t('booking.timeOfDay.day')}
                  slots={groupedSlots.day}
                  selectedSlot={selectedSlot}
                  onSelect={onSlotSelect}
                  formatTime={formatSlotTime}
                  loading={slotsLoading}
                />
              )}
              {(groupedSlots.evening.length > 0 || slotsLoading) && (
                <TimeSlotSection
                  title={t('booking.timeOfDay.evening')}
                  slots={groupedSlots.evening}
                  selectedSlot={selectedSlot}
                  onSelect={onSlotSelect}
                  formatTime={formatSlotTime}
                  loading={slotsLoading}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
